import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Box, Grid } from "@mui/material";

// Import dei tuoi componenti
import { ChipTextField } from "../../../custom-components/Editor/KeyMomentForm/ChipTextField";
//import { MultiChipTextField } from "./MultiChipTextField";
import CustomAutoComplete from "../CommonComponents/CustomAutoComplete";
import MatchSearch from "../CommonComponents/SearchAutocomplete/MatchSearch";
import {Condition} from "../../../types/ruleTypes";
import TextField from "@mui/material/TextField";
import TeamSearch from "@/pages/custom-components/CommonComponents/SearchAutocomplete/TeamSearch";
import {DateTimePicker} from "@mui/x-date-pickers/DateTimePicker";
import {MultiChipTextField} from "@/pages/custom-components/CommonComponents/MultiChipTextField";
import { useIntl } from "react-intl";
import MatchDaySearch from "@/pages/custom-components/CommonComponents/SearchAutocomplete/MatchDaySearch";

export interface RuleConditionField {
    source: string;
    options: string[] | any[];
    type: "multistring" | "int" | "datetime" | "string" | "multiobject";
    name: string;
    fieldType?: string;
}

interface RuleConditionFormProps {
    schema: RuleConditionField[];
    defaultValues?: Record<string, any>;
    onChange: (key: string, value: any) => void;
    gridSizes?: Record<"xs" | "sm" | "md" | "lg" | "xl", number>;
}

export const RuleConditionForm: React.FC<RuleConditionFormProps> = ({
                                                                        schema,
                                                                        defaultValues = {},
                                                                        onChange,
                                                                        // Imposta il tuo layout originale come fallback di default
                                                                        gridSizes = { xs: 12, sm: 6, md: 4, lg: 3 }
                                                                    }) => {
    const { control, reset } = useForm({
        defaultValues,
        mode: "onChange",
    });

    const intl = useIntl();

    useEffect(() => {
        if (defaultValues && Object.keys(defaultValues)?.length > 0) {
            reset(defaultValues);
        }
    }, [defaultValues, reset]);

    return (
        <Box component="form" sx={{ width: "100%" }}>
            <Grid container spacing={2}>
                {schema.map((fieldDef) => {
                    const isRemoteSearch = fieldDef.source !== "local";

                    return (
                        <Grid
                            // Usa la prop dinamica invece di valori hardcoded
                            size={gridSizes}
                            key={fieldDef.name}
                        >
                            <Controller
                                name={fieldDef.name}
                                control={control}
                                render={({ field }) => {
                                    const handleChange = (val: any) => {
                                        field.onChange(val);
                                        onChange(fieldDef.name, val);
                                    };

                                    // 1. Caso: Ricerca Remota (es: source: "events", "competitions", "teams")
                                    // Nota: Qui sto usando MatchSearch come placeholder.
                                    // Se hai componenti diversi (EventSearch, TeamSearch), dovrai fare uno switch
                                    // basato su fieldDef.source per renderizzare quello corretto.
                                    if (isRemoteSearch && fieldDef.source === "events") {
                                        return (
                                            <MatchSearch
                                                label={fieldDef.name.charAt(0).toUpperCase() + fieldDef.name.slice(1)}
                                                value={field.value || null}
                                                onChange={handleChange}
                                                freeSolo={false}
                                                multiple={true}
                                                // Se MatchSearch supporta multiple, passalo.
                                                // Nel tuo esempio MatchSearch era multiple={false} fisso,
                                                // ma il json dice type: "multistring" per events.
                                                // Assicurati che i tipi combacino con le capacità del componente!
                                            />
                                        );
                                    }

                                    if (isRemoteSearch && fieldDef.source === 'matchdays') {
                                        const label = intl.formatMessage({id: fieldDef.source, defaultMessage: fieldDef.source})
                                        return (
                                            <MatchDaySearch
                                                label={label}
                                                value={field.value}
                                                onChange={handleChange}
                                                freeSolo={true}
                                                multiple={true}
                                            />
                                        )
                                    }

                                    if (fieldDef.name === 'periods') {

                                    }

                                    if (isRemoteSearch && fieldDef.source === "teams") {
                                        return (
                                            <TeamSearch
                                                label={fieldDef.name.charAt(0).toUpperCase() + fieldDef.name.slice(1)}
                                                value={field.value || null}
                                                onChange={handleChange}
                                                freeSolo={false}
                                                multiple={true}
                                            />
                                        );
                                    }

                                    // 2. Caso: Sorgente Locale con Opzioni (es: source: "local", type: "multistring")
                                    if (fieldDef.fieldType !== undefined && fieldDef.fieldType === 'search_multistring') {

                                        let formattedOptions = [...fieldDef.options]
                                        if(field.name === 'periods' || field.name === 'key_moment_types') {
                                            formattedOptions = formattedOptions.map(opt => ({
                                                id: opt,
                                                value: intl.formatMessage({ id: opt, defaultMessage: opt }) // Mostra la stringa tradotta nella UI
                                            }));
                                        }

                                        return (
                                            <CustomAutoComplete
                                                name={fieldDef.name}
                                                label={(fieldDef.name.charAt(0).toUpperCase() + fieldDef.name.slice(1)).replaceAll('_', ' ')}
                                                options={formattedOptions}
                                                value={field.value || []}
                                                onChange={(name: string, val: any) => handleChange(val)}
                                                multiple={(fieldDef.type === "multistring") || (fieldDef.type === 'multiobject')}
                                                freeSolo={false}
                                                // Usa chipVariant se vuoi lo stile speciale che hai definito
                                                chipVariant="pill"
                                                valueField={"id"}
                                                labelField={"value"}
                                            />
                                        );
                                    }

                                    // 3. Caso: Input Testuali Semplici / Numeri (Senza opzioni fisse o ricerche remote complesse)
                                    switch (fieldDef.type) {
                                        case "int": {
                                            let minValue = undefined;
                                            let maxValue = undefined;

                                            if (fieldDef?.filter && fieldDef?.filter?.value !== undefined) {
                                                const val = fieldDef?.filter?.value;
                                                switch (fieldDef?.filter.op) {
                                                    case 'ge': // Greater than or equal (>=)
                                                        minValue = val;
                                                        break;
                                                    case 'gt': // Greater than (>) -> essendo int, aggiungiamo 1
                                                        minValue = val + 1;
                                                        break;
                                                    case 'le': // Less than or equal (<=)
                                                        maxValue = val;
                                                        break;
                                                    case 'lt': // Less than (<) -> essendo int, sottraiamo 1
                                                        maxValue = val - 1;
                                                        break;
                                                    case 'eq': // Equal (==)
                                                        minValue = val;
                                                        maxValue = val;
                                                        break;
                                                }
                                            }

                                            // 2. Mantieni il fallback originale per "period" se non c'è un limite minimo esplicito
                                            if (fieldDef.name === 'period' && minValue === undefined) {
                                                minValue = 0;
                                            }

                                            return (
                                                <TextField
                                                    label={fieldDef.name === 'period' ? 'Repeat every' : fieldDef.name === 'delay' ? 'Delay' : fieldDef.name}
                                                    // Usa "text" per evitare i comportamenti imprevisti del type="number" (es. la lettera 'e')
                                                    type="text"
                                                    // inputMode="numeric" apre il tastierino coi numeri su mobile
                                                    inputMode="numeric"
                                                    InputProps={{
                                                        inputProps: {
                                                            min: minValue,
                                                            max: maxValue
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.target.blur();
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        if (e.target.value === "" || e.target.value === "-") return;

                                                        let val = Number(e.target.value);

                                                        if (minValue !== undefined && val < minValue) {
                                                            val = minValue;
                                                        }
                                                        if (maxValue !== undefined && val > maxValue) {
                                                            val = maxValue;
                                                        }

                                                        // Riapplica la formattazione se il numero è cambiato per via dei limiti
                                                        // o per rimuovere gli zeri iniziali (es: "0444" -> 444)
                                                        if (String(val) !== e.target.value) {
                                                            handleChange(val);
                                                        }
                                                    }}
                                                    sx={{ width: '100%' }}
                                                    value={field.value ?? ""}
                                                    onChange={(e) => {
                                                        let val = e.target.value;

                                                        // Capiamo se i numeri negativi sono permessi
                                                        const allowNegative = minValue === undefined || minValue < 0;

                                                        // REGEX:
                                                        // Se allowNegative è true, rimuove tutti i caratteri che NON sono numeri,
                                                        // ECCETTO un segno '-' se si trova esattamente all'inizio della stringa.
                                                        // Se allowNegative è false, rimuove qualsiasi cosa non sia un numero.
                                                        const regex = allowNegative ? /(?!^-)[^0-9]/g : /[^0-9]/g;

                                                        // Sanitizza la stringa in tempo reale (blocca incolla errati e tastiere mobile)
                                                        let sanitizedVal = val.replace(regex, "");

                                                        // Previene errori `NaN` passando la stringa se è in fase di digitazione iniziale
                                                        handleChange(sanitizedVal === "-" || sanitizedVal === "" ? sanitizedVal : Number(sanitizedVal));
                                                    }}
                                                />
                                            );
                                        }
                                        case 'datetime':
                                            return (
                                                <DateTimePicker
                                                    label={field.name ? field.name.charAt(0).toUpperCase() + field.name.slice(1) : ''}
                                                    value={field.value || null}
                                                    onChange={(newValue) => {
                                                        handleChange(newValue);
                                                    }}
                                                    slotProps={{
                                                        textField: {
                                                            sx: { width: '100%' }, // La larghezza va sul contenitore esterno
                                                            InputProps: {
                                                                sx: {
                                                                    height: '41px', // <-- La tua altezza precisa qui
                                                                    // Ricalibriamo l'input testuale per non tagliare il testo
                                                                    '& .MuiInputBase-input': {
                                                                        height: '100%',
                                                                        padding: '0 14px',
                                                                        boxSizing: 'border-box'
                                                                    }
                                                                }
                                                            },
                                                            // OPZIONALE: Se la label non è centrata verticalmente (succede con altezze < 50px)
                                                            InputLabelProps: {
                                                                sx: {
                                                                    lineHeight: 'auto',
                                                                    top: '-4px', // Aggiusta questo valore (es. -2px, -6px) finché la label non è centrata
                                                                    '&.MuiInputLabel-shrink': {
                                                                        top: '0px', // Riporta la label al suo posto quando è in alto
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }}
                                                />
                                            )
                                        case "string":
                                            return (
                                                <ChipTextField
                                                    label={fieldDef.name}
                                                    value={field.value || ""}
                                                    onChange={handleChange}
                                                    type="text"
                                                />
                                            );

                                        case "multistring":
                                            // Se per qualche motivo arriva qui (non ha options e non è un search remoto gestito),
                                            // facciamo inserire stringhe libere
                                            return (
                                                <MultiChipTextField
                                                    label={fieldDef.name}
                                                    values={field.value || []}
                                                    onChange={handleChange}
                                                />
                                            );

                                        default:
                                            return null;
                                    }
                                }}
                            />
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};